import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// MilestoneCelebration — premium gold confetti burst that fires when a user
// unlocks a new Glow Journey milestone. Pure CSS particles, no dependency.
// • Auto-dismisses after 2800ms
// • Tap anywhere to dismiss early
// • Triggers a short haptic on devices that support navigator.vibrate
// • Honors the `enabled` flag (settings toggle) — if disabled, renders nothing
// ─────────────────────────────────────────────────────────────────────────────

function buildParticles(count) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + (Math.random() * 0.6 - 0.3);
    const distance = 140 + Math.random() * 220;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance - 40 - Math.random() * 80; // bias upward
    const rot = Math.random() * 720 - 360;
    const delay = Math.random() * 90;
    const dur = 1400 + Math.random() * 900;
    const size = 6 + Math.random() * 8;
    const palette = ["#e8d3b9", "#c9ad84", "#f6e7cf", "#ffffff", "#b08a4d"];
    const color = palette[Math.floor(Math.random() * palette.length)];
    out.push({ dx, dy, rot, delay, dur, size, color, key: i });
  }
  return out;
}

export default function MilestoneCelebration({ milestone, enabled = true, onDone }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!milestone || !enabled) return;
    setShow(true);
    // Haptic — Android / some browsers only. Silent failure on iOS Safari.
    try {
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        navigator.vibrate([18, 40, 28]);
      }
    } catch { /* ignore */ }
    const t = setTimeout(() => {
      setShow(false);
      if (onDone) onDone();
    }, 2800);
    return () => clearTimeout(t);
  }, [milestone, enabled, onDone]);

  const particles = useMemo(() => buildParticles(36), [milestone?.id]);

  if (!milestone || !enabled || !show) return null;

  const overlay = (
    <div
      className="milestone-celebration"
      data-testid="milestone-celebration"
      onClick={() => { setShow(false); if (onDone) onDone(); }}
      role="dialog"
      aria-label="Milestone unlocked"
    >
      <div className="milestone-celebration-inner">
        <div className="milestone-particles" aria-hidden="true">
          {particles.map((p) => (
            <span
              key={p.key}
              className="milestone-particle"
              style={{
                width: p.size,
                height: p.size,
                background: p.color,
                animation: `milestoneFly ${p.dur}ms cubic-bezier(.18,.7,.2,1) ${p.delay}ms forwards`,
                ["--dx"]: `${p.dx}px`,
                ["--dy"]: `${p.dy}px`,
                ["--rot"]: `${p.rot}deg`,
              }}
            />
          ))}
        </div>

        <div className="milestone-card" data-testid="milestone-celebration-card">
          <div className="milestone-card-icon">
            <Sparkles size={20} strokeWidth={1.8} />
          </div>
          <div className="milestone-card-eyebrow">MILESTONE UNLOCKED</div>
          <div className="milestone-card-name serif">{milestone.name}</div>
          {milestone.sub && <div className="milestone-card-sub">{milestone.sub}</div>}
          <div className="milestone-card-cta">Tap to continue</div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
